import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, CheckCircle2, Sparkles } from "lucide-react";

const FEATURES = [
  "Apply to unlimited safety jobs for 30 days",
  "Save your profile, resume & cover letter once",
  "One-click apply to any listing",
  "Applications emailed to employers & tracked",
  "Browsing is always free — pay only to apply",
];

export default function Membership() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    if (user?.role === "applicant") api.get("/applicant/profile").then(({ data }) => setStatus(data)).catch(() => {});
  }, [user]);

  const buy = async () => {
    if (!user) return navigate("/login", { state: { redirect: "/membership" } });
    if (user.role !== "applicant") return toast.error("Membership is for applicant accounts.");
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key: "applicant_membership", origin_url: window.location.origin, purpose: "membership" });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); setBusy(false); }
  };

  const active = status?.membership_active;

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <div className="grid-bg flex-1">
        <div className="px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto py-16">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-100 text-amber-800 text-xs font-semibold uppercase tracking-wide"><Sparkles className="h-3.5 w-3.5" /> Applicant Membership</div>
            <h1 className="font-display text-4xl sm:text-5xl font-extrabold text-navy-800 mt-4">Apply to every safety job for $10</h1>
            <p className="text-slate-500 mt-3 max-w-xl mx-auto">One flat price. 30 days of unlimited applications. No per-application fees.</p>
          </div>

          <div className="bg-navy-800 rounded-2xl p-8 sm:p-10 shadow-xl text-white relative overflow-hidden max-w-lg mx-auto">
            <div className="absolute top-0 right-0 h-48 w-48 bg-amber-500/20 rounded-full blur-3xl" />
            <div className="flex items-baseline gap-2"><span className="font-display text-6xl font-extrabold">$10</span><span className="text-slate-300">/ 30 days</span></div>
            <ul className="mt-8 space-y-3.5">
              {FEATURES.map((f) => <li key={f} className="flex gap-3 text-slate-200"><CheckCircle2 className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" /> {f}</li>)}
            </ul>
            {active ? (
              <div className="mt-8 p-4 rounded-xl bg-emerald-500/15 border border-emerald-400/30 text-center" data-testid="membership-active-banner">
                <div className="font-semibold text-emerald-300">Your membership is active</div>
                <div className="text-xs text-slate-300 mt-1">Valid until {new Date(status.membership_active_until).toLocaleDateString()}</div>
                <Button onClick={() => navigate("/jobs")} className="mt-4 bg-amber-500 hover:bg-amber-600 text-navy-900 font-bold">Browse jobs</Button>
              </div>
            ) : (
              <Button data-testid="applicant-membership-checkout-button" onClick={buy} disabled={busy} className="w-full mt-8 h-12 bg-amber-500 hover:bg-amber-600 text-navy-900 font-bold text-base">
                {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Get membership — $10"}
              </Button>
            )}
            {loading && <p className="text-center text-xs text-slate-400 mt-3">Checking your account…</p>}
          </div>
          <p className="text-center text-xs text-slate-400 mt-6">Secure payment via Stripe. Renews only if you choose to.</p>
        </div>
      </div>
      <Footer />
    </div>
  );
}
